import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box, Input, InputGroup, InputRightElement, Button, Text, Spinner,
  PinInput, PinInputField, HStack
} from "@chakra-ui/react";
import { verifyForgotPasswordCode, resetPassword, sendForgotPasswordCode } from "auth/gridAuthClient";
import EyeOpen from "assets/svg/eye-open.svg";
import EyeClose from "assets/svg/eye-close.svg";

const FIELD_W = "260px";
const inputSt = {
  bg: "transparent", border: "1px solid #fff", borderRadius: "10px",
  color: "#fff", fontSize: "13px", p: "10px", w: "100%"
};

// Move PasswordInput outside to prevent recreation on every render
const PasswordInput = ({ field, placeholder, showField, state, updateState }) => {
  // Use callback to prevent focus loss
  const handleToggle = useCallback((e) => {
    e.preventDefault(); // Prevent any default behavior
    e.stopPropagation(); // Stop event bubbling
    updateState({ [showField]: !state[showField] });
  }, [showField, state, updateState]);

  const handleChange = useCallback((e) => {
    updateState({ [field]: e.target.value, error: "" });
  }, [field, updateState]);

  return (
    <InputGroup w={FIELD_W} mb={field === "newPassword" ? "18px" : "4px"}>
      <Input 
        {...inputSt} 
        type={state[showField] ? "text" : "password"} 
        placeholder={placeholder}
        required 
        pr="3rem" 
        value={state[field]} 
        onChange={handleChange}
        autoComplete={field === "newPassword" ? "new-password" : "new-password"}
      />
      <InputRightElement width="3rem" height="100%">
        <Button
          h="1.5rem" 
          minW="unset"
          p={0}
          display="flex" 
          alignItems="center" 
          justifyContent="center"
          bg="transparent" 
          onMouseDown={handleToggle} // Use onMouseDown to prevent focus loss
          cursor="pointer"
          _hover={{ bg: "transparent" }}
          _active={{ bg: "transparent" }}
          _focus={{ boxShadow: "none" }}
          tabIndex={-1} // Prevent tab focus
        >
          <img src={state[showField] ? EyeClose : EyeOpen} alt="toggle visibility" width={20} />
        </Button>
      </InputRightElement>
    </InputGroup>
  );
};

export default function ForgotPasswordVerification({ email, onBack, onSuccess }) {
  const isMountedRef = useRef(true);
  const [timer, setTimer] = useState(60);
  const intervalRef = useRef(null);
  const [state, setState] = useState({
    code: "", newPassword: "", confirmPassword: "", error: "", isLoading: false,
    isVerified: false, userId: null, showPassword: false, showConfirmPassword: false, canResend: false
  });

  // Use callback to prevent recreation
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    startTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      isMountedRef.current = false;
    };
  }, []);

  const startTimer = () => {
    setTimer(60);
    updateState({ canResend: false });
    if (intervalRef.current) clearInterval(intervalRef.current);
    
    intervalRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          if (isMountedRef.current) updateState({ canResend: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleCodeChange = (value) => {
    updateState({ code: value, error: "" });
    if (value.length === 6) verifyCode(value);
  };

  const verifyCode = async (code) => {
    updateState({ isLoading: true, error: "" });
    
    try {
      const res = await verifyForgotPasswordCode(email, code);
      console.log("Verification response:", res);
      
      if (res?.status === "200" || res?.status === 200 || res?.response) {
        const userId = res?.response?.newId || res?.newId || res?.response?.userId || 
                      res?.response?.id || res?.response?.user?.id || res?.response?.user?.userId ||
                      (typeof res?.response === 'number' ? res.response : null) || 
                      res?.userId || res?.id;
        
        console.log("Extracted userId:", userId);
        
        if (!userId && userId !== 0) throw new Error("Could not extract user ID from verification response");
        
        if (isMountedRef.current) updateState({ isVerified: true, userId, isLoading: false });
      } else {
        throw new Error(res?.errorMessage || "Invalid verification code");
      }
    } catch (err) {
      console.error("Verification error:", err);
      if (isMountedRef.current) {
        updateState({ error: err.message || "Invalid verification code. Please try again.", isLoading: false, code: "" });
      }
    }
  };

  const handleResendCode = async () => {
    if (!state.canResend) return;
    updateState({ isLoading: true, error: "" });
    
    try {
      const res = await sendForgotPasswordCode(email);
      if (res?.status === "200" || res?.status === 200 || res?.response) {
        if (isMountedRef.current) {
          updateState({ isLoading: false, error: "", code: "" });
          startTimer();
        }
      } else throw new Error(res?.errorMessage || "Failed to resend code");
    } catch (err) {
      if (isMountedRef.current) {
        updateState({ error: err.message || "Failed to resend code. Please try again.", isLoading: false });
      }
    }
  };

  const validatePassword = (pwd) => {
    if (pwd.length < 10) return "Password must be at least 10 characters long.";
    if (!/[A-Z]/.test(pwd)) return "Password must contain at least one capital letter.";
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) return "Password must contain at least one special character.";
    return null;
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    updateState({ error: "" });

    const { newPassword: pwd, confirmPassword: confirm, userId } = state;
    
    if (!pwd || !confirm) return updateState({ error: "Please fill in all fields." });
    if (pwd !== confirm) return updateState({ error: "Passwords do not match." });
    
    const pwdError = validatePassword(pwd);
    if (pwdError) return updateState({ error: pwdError });

    updateState({ isLoading: true });

    try {
      console.log("Resetting password with userId:", userId, "password length:", pwd.length);
      
      if (!userId) throw new Error("User ID is missing. Please try the verification process again.");
      
      const res = await resetPassword(userId, pwd);
      console.log("Reset password response:", res);
      
      if (res?.status === "200" || res?.status === 200 || res?.response || res?.message) {
        if (isMountedRef.current) onSuccess();
      } else throw new Error(res?.errorMessage || "Failed to reset password");
    } catch (err) {
      if (isMountedRef.current) {
        updateState({ error: err.message || "Failed to reset password. Please try again.", isLoading: false });
      }
    }
  };

  if (state.isVerified) {
    return (
      <Box as="form" onSubmit={handlePasswordReset} display="flex" flexDirection="column" alignItems="center" px="25px">
        <Text color="#fff" fontSize="26px" mb="6px">Reset Password</Text>
        <Text color="#ADA8C3" fontSize="12px" mb="28px" textAlign="center">
          Please enter your new password
        </Text>

        <PasswordInput 
          field="newPassword" 
          placeholder="New Password *" 
          showField="showPassword"
          state={state}
          updateState={updateState}
        />
        <PasswordInput 
          field="confirmPassword" 
          placeholder="Confirm Password *" 
          showField="showConfirmPassword"
          state={state}
          updateState={updateState}
        />

        <Text color="#ADA8C3" fontSize="10px" mb="12px" textAlign="center">
          Password must be at least 10 characters with a capital letter and special character
        </Text>

        {state.error && (
          <Text color="red" fontSize="12px" mt="8px" mb="8px" textAlign="center" fontWeight="bold">
            {state.error}
          </Text>
        )}

        {state.isLoading ? (
          <Spinner color="white" mt="15px" w="30px" h="30px" thickness="4px" speed="0.65s" />
        ) : (
          <Button type="submit" w={FIELD_W} mt="15px" p="10px" bg="#fff" color="#000" borderRadius="10px"
            disabled={!state.newPassword || !state.confirmPassword || state.isLoading}
            _hover={{ cursor: "pointer", bg: "transparent", color: "#fff", outline: "1px solid #fff" }}>
            Reset Password
          </Button>
        )}

        <Text fontSize="12px" color="#fff" mt="15px" textAlign="center">
          <Text as="span" fontWeight="bold" color="#fff" cursor="pointer" onClick={onBack} _hover={{ opacity: 0.8 }}>
            Back
          </Text>
        </Text>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" px="25px">
      <Text color="#fff" fontSize="26px" mb="6px">Email Verification</Text>
      <Text color="#ADA8C3" fontSize="12px" mb="28px" textAlign="center">
        We've sent a verification code to<br />
        <Text as="span" color="#fff" fontWeight="bold">{email}</Text>
      </Text>

      <HStack mb="18px" spacing="8px">
        <PinInput value={state.code} onChange={handleCodeChange} size="md" isDisabled={state.isLoading}>
          {[...Array(6)].map((_, i) => (
            <PinInputField key={i} 
              bg="transparent" 
              border="1px solid #fff" 
              borderRadius="8px"
              color="#fff" 
              fontSize="16px" 
              w="35px" 
              h="35px" 
              textAlign="center"
              _hover={{ borderColor: "#ADA8C3" }}
              _focus={{ borderColor: "#fff", boxShadow: "0 0 0 1px #fff" }}
              _placeholder={{ color: "transparent" }}
              sx={{
                '&::placeholder': {
                  color: 'transparent !important'
                }
              }}
            />
          ))}
        </PinInput>
      </HStack>

      {state.error && (
        <Text color="red" fontSize="12px" mt="8px" mb="8px" textAlign="center" fontWeight="bold">
          {state.error}
        </Text>
      )}

      {state.isLoading && (
        <Spinner color="white" mt="15px" w="30px" h="30px" thickness="4px" speed="0.65s" />
      )}

      <Text fontSize="12px" color="#ADA8C3" mt="15px" textAlign="center">
        {state.canResend ? (
          <>
            Didn't receive code?{" "}
            <Text as="span" fontWeight="bold" color="#fff" cursor="pointer"
              onClick={handleResendCode} _hover={{ opacity: 0.8 }}>
              Resend Code
            </Text>
          </>
        ) : (
          <>Resend code in {timer} seconds</>
        )}
      </Text>

      <Text fontSize="12px" color="#fff" mt="15px" textAlign="center">
        <Text as="span" fontWeight="bold" color="#fff" cursor="pointer" onClick={onBack} _hover={{ opacity: 0.8 }}>
          Back
        </Text>
      </Text>
    </Box>
  );
}